import { useEffect, useRef, useState } from "react"
import type { KeyboardEvent } from "react"
import { CloseIcon } from "../../icons"

type TagOption = { value: string; label: string }

type TagSearchInputProps = {
  value: string
  onChange: (value: string) => void
  options: TagOption[]
  placeholder?: string
  disabled?: boolean
}

export default function TagSearchInput({ value, onChange, options, placeholder, disabled }: TagSearchInputProps) {
  const [query, setQuery] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)

  const selected = options.find((o) => o.value === value) ?? (value ? { value, label: value } : undefined)

  const normalizedQuery = query.trim().toLowerCase()
  const filtered = normalizedQuery ? options.filter((o) => o.label.toLowerCase().includes(normalizedQuery)) : options
  const showCustomOption = normalizedQuery.length > 0 && !options.some((o) => o.label.toLowerCase() === normalizedQuery)

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [])

  function commit(v: string) {
    onChange(v)
    setQuery("")
    setIsOpen(false)
    setFocusedIndex(-1)
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    const optionCount = filtered.length + (showCustomOption ? 1 : 0)
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setIsOpen(true)
      setFocusedIndex((i) => (i + 1 < optionCount ? i + 1 : 0))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setFocusedIndex((i) => (i > 0 ? i - 1 : optionCount - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (focusedIndex >= 0 && focusedIndex < filtered.length) {
        commit(filtered[focusedIndex].value)
      } else if (focusedIndex === filtered.length && showCustomOption) {
        commit(query.trim())
      } else if (filtered.length === 1) {
        commit(filtered[0].value)
      }
    } else if (e.key === "Escape") {
      setIsOpen(false)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex min-h-11 w-full flex-wrap items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-1.5 focus-within:border-[var(--alas-blue)] focus-within:ring-2 focus-within:ring-[var(--alas-blue)]/20 dark:border-gray-700 dark:bg-gray-900 ${
          disabled ? "opacity-60" : ""
        }`}
      >
        {selected ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
            {selected.label}
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange("")}
                aria-label="Quitar categoría"
                className="text-brand-400 hover:text-brand-600"
              >
                <CloseIcon className="size-3" />
              </button>
            )}
          </span>
        ) : (
          <input
            disabled={disabled}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setIsOpen(true)
              setFocusedIndex(-1)
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="min-w-[120px] flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400 dark:text-white"
          />
        )}
      </div>

      {isOpen && !selected && (filtered.length > 0 || showCustomOption) && (
        <div className="absolute z-40 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900">
          {filtered.map((o, i) => (
            <button
              key={o.value}
              type="button"
              onClick={() => commit(o.value)}
              className={`flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800 ${
                focusedIndex === i ? "bg-gray-50 dark:bg-gray-800" : ""
              }`}
            >
              {o.label}
            </button>
          ))}
          {showCustomOption && (
            <button
              type="button"
              onClick={() => commit(query.trim())}
              className={`flex w-full items-center gap-1 border-t border-gray-100 px-3 py-2 text-left text-sm text-brand-600 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800 ${
                focusedIndex === filtered.length ? "bg-gray-50 dark:bg-gray-800" : ""
              }`}
            >
              Usar "{query.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  )
}
